// SPDX-License-Identifier: AGPL-3.0-or-later

import {SettingsSection} from '@app/features/guild/components/modals/guild_tabs/guild_overview_tab/components/SettingsSection';
import styles from '@app/features/guild/components/modals/guild_tabs/guild_overview_tab/GuildOverviewTab.module.css';
import type {ChannelLike} from '@app/features/guild/components/modals/guild_tabs/guild_overview_tab/GuildOverviewTypes';
import type {FormInputs, SelectOption} from '@app/features/guild/utils/guild_tabs/GuildOverviewTabUtils';
import {afkTimeoutOptionsRaw} from '@app/features/guild/utils/guild_tabs/GuildOverviewTabUtils';
import {Select as FormSelect} from '@app/features/ui/components/form/Select';
import {Trans, useLingui} from '@lingui/react/macro';
import type React from 'react';
import {useMemo} from 'react';
import type {UseFormReturn} from 'react-hook-form';
import {Controller} from 'react-hook-form';
export const IdleSettingsSection: React.FC<{
	form: UseFormReturn<FormInputs>;
	canManageGuild: boolean;
	voiceChannels: Array<ChannelLike>;
}> = ({form, canManageGuild, voiceChannels}) => {
	const {t, i18n} = useLingui();
	const afkTimeoutOptions = useMemo<Array<{value: number; label: string}>>(
		() => afkTimeoutOptionsRaw.map(({value, label}) => ({value, label: i18n._(label)})),
		[i18n],
	);
	const afkChannelOptions = useMemo<Array<SelectOption>>(
		() => [
			{value: null, label: t`No AFK Channel`},
			...voiceChannels.map((channel) => ({value: channel.id, label: channel.name ?? t`Unnamed channel`})),
		],
		[voiceChannels],
	);
	return (
		<SettingsSection
			title={<Trans>Idle Settings</Trans>}
			description={<Trans>Configure AFK channel and timeout</Trans>}
		>
			<div className={styles.settingsContent}>
				<div>
					<Controller
						name="afk_channel_id"
						control={form.control}
						render={({field}) => (
							<FormSelect<string | null>
								label={t`AFK / Idle Channel`}
								description={t`Move members to this channel when they're AFK.`}
								value={field.value ?? null}
								onChange={(v) => field.onChange(v)}
								options={afkChannelOptions}
								placeholder={t`Select a channel`}
								disabled={!canManageGuild}
							/>
						)}
					/>
				</div>
				<div>
					<Controller
						name="afk_timeout"
						control={form.control}
						render={({field}) => (
							<FormSelect<number>
								label={t`AFK Timeout`}
								value={field.value ?? 300}
								onChange={(v) => field.onChange(v)}
								options={afkTimeoutOptions}
								disabled={!canManageGuild}
							/>
						)}
					/>
				</div>
			</div>
		</SettingsSection>
	);
};
